<template>
  <Card>
    <CardContent class="flex flex-col gap-1 px-5 py-3">
      <div class="flex items-start justify-between gap-4">
        <h1 class="text-lg font-semibold tracking-tight text-gray-900">{{ title }}</h1>

        <div v-if="isLive" class="flex shrink-0 items-center gap-2">
          <span class="relative flex h-2 w-2" aria-hidden="true">
            <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75"></span>
            <span class="relative inline-flex h-2 w-2 rounded-full bg-green-500"></span>
          </span>
          <Badge variant="green" :capitalize="false">Live</Badge>
        </div>
        <span
          v-else
          class="shrink-0 text-sm tabular-nums text-muted-foreground"
          :class="{ 'font-medium text-gray-700': !isPast }"
        >
          {{ relativeTimeLabel }}
        </span>
      </div>

      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
        <span class="flex items-center gap-1.5 tabular-nums">
          <vue-feather type="clock" class="!h-3.5 !w-3.5 shrink-0" />
          <span>{{ formattedWhen }}</span>
        </span>

        <component
          :is="locationInfo?.href ? 'a' : 'span'"
          v-if="locationInfo"
          :href="locationInfo.href"
          :target="locationInfo.href?.startsWith('http') ? '_blank' : undefined"
          :rel="locationInfo.href?.startsWith('http') ? 'noopener noreferrer' : undefined"
          class="flex min-w-0 items-center gap-1.5"
          :class="locationInfo.href ? 'text-indigo-600 hover:text-indigo-700 hover:underline' : ''"
        >
          <vue-feather :type="locationInfo.icon" class="!h-3.5 !w-3.5 shrink-0" />
          <span class="truncate">{{ locationInfo.label }}</span>
        </component>
      </div>

      <div v-if="$slots.attendees">
        <slot name="attendees" />
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useMeetingUsher, type MeetingDashboardArgs } from '@/composables/useMeetingUsher';
import RailsUtility from '@/utilities/rails';
import { formatStandardDate } from '@/utilities/format';
import DateUtility from '@/utilities/dates';
import { formatDistance } from 'date-fns';

type ContactLike = {
  name: string;
};

const props = defineProps<{
  contactEvent: MeetingDashboardArgs;
  contact: ContactLike;
}>();

const meetingUsher = useMeetingUsher();

const title = computed(() => {
  const typeLabel = RailsUtility.convertFromCasedEnumFormat(props.contactEvent.event_type);
  return `${typeLabel} with ${props.contact.name}`;
});

const formattedWhen = computed(() => {
  const start = DateUtility.formatDate(props.contactEvent.start_time, 'h:mm a');
  const end = DateUtility.formatDate(props.contactEvent.end_time, 'h:mm a');
  const date = formatStandardDate(props.contactEvent.start_time);
  return `${start} – ${end} · ${date}`;
});

const isLive = computed(() => meetingUsher.isLive(props.contactEvent));
const isPast = computed(() => meetingUsher.isPastEvent(props.contactEvent));

const relativeTimeLabel = computed(() => {
  const distance = formatDistance(props.contactEvent.start_time, new Date(), { addSuffix: true });
  return isPast.value ? distance : `Starts ${distance}`;
});

const locationInfo = computed(() => meetingUsher.getLocationInfo(props.contactEvent));
</script>
